import { createSelector } from '@reduxjs/toolkit';
import { uniqBy } from 'lodash';
import { Maybe, UserType } from 'src/graphql';
import { SYSTEM_PERMISSIONS } from 'src/permissionsTypes';
import { selectCurrentUser } from 'src/selectors/selectCurrentUser';
import { TRootState } from 'src/store';
import { CaseLead, CaseTeamMemberRole } from 'src/types';

export const caseTeamPanel = createSelector(
  (state: TRootState) => state.case.caseById,
  selectCurrentUser,
  (caseById, currentUser) => {
    if (!caseById)
      return {
        canEditCaseLead: false,
        canViewInvitationDetails: false,
        canViewInviteButton: false,
        canInviteToCase: false,
        availableCaseLeads: [],
        caseTeamListItems: [],
        caseTeamMembersCount: 0,
        caseLeadId: -1,
        caseLeadLabel: '',
        caseId: '',
        canEditPermissions: false,
      };

    const caseLead = caseById?.CaseLead;
    const isCaseLead = String(caseLead?.id) === String(currentUser?.id);

    const { canAdministerCasePermissions } =
      caseById.AssociatedPermissions ?? {};

    const report = caseById?.Reports?.[0];

    const { canAdministerReportPermissions } =
      report?.AssociatedPermissions ?? {};

    const canEditCaseLead = Boolean(
      canAdministerCasePermissions && canAdministerReportPermissions,
    );

    const availableCaseLeads = getAvailableCaseLeads(caseById?.AllCaseLeads);

    const caseTeam =
      caseById?.CaseTeam?.map((member) => ({
        ...member,
        role: CaseTeamMemberRole.TEAM,
      })) ?? [];

    const caseTeamList = uniqBy(caseTeam, 'id').filter(
      (member) => member.id !== caseLead?.id,
    );

    const fullCaseTeamList = caseLead
      ? uniqBy(
          [{ ...caseLead, role: CaseTeamMemberRole.LEAD }, ...caseTeamList],
          'id',
        )
      : caseTeamList;

    const caseLeadLabel = getCaseLeadLabel(caseLead);

    const uniqueCaseTeamMemberIds = [
      ...new Set(fullCaseTeamList.map((member) => member.id)),
    ];

    const teamMemberDisplayList = canEditCaseLead
      ? caseTeamList
      : fullCaseTeamList;

    const caseTeamListItems = teamMemberDisplayList.map((user) => ({
      name: user.name ?? '',
      description: user.site ?? '',
      badgeText: user.role,
      id: user.id,
      isRowEnabled: user.id == currentUser?.id || canEditCaseLead,
    }));

    const canInviteToCase = isCaseLead;

    return {
      canEditCaseLead,
      canInviteToCase,
      canViewInvitationDetails: true,
      canViewInviteButton: true,
      availableCaseLeads,
      caseTeamListItems,
      caseTeamMembersCount: uniqueCaseTeamMemberIds.length,
      caseLeadLabel,
      caseLeadId: getCaseLeadId(caseLead),
      caseId: caseById?.id,
    };
  },
);

export const getCaseLeadForDisplay = (caseLead: CaseLead) => ({
  label: caseLead?.name ?? 'Unnamed User',
  value: Number(caseLead?.id),
});

export const getAvailableCaseLeads = (
  caseManagers?: Maybe<Partial<UserType>[]>,
) => caseManagers?.map(getCaseLeadForDisplay) ?? [];

export const getAvailableCaseLeadsExcludingReporters = (
  caseManagers?: Maybe<Partial<UserType>[]>,
  reporterIds?: (number | undefined)[],
) => {
  const filteredManagers = caseManagers?.filter(
    (manager) => !reporterIds?.includes(manager.id),
  );
  return filteredManagers?.map(getCaseLeadForDisplay) ?? [];
};

export const getCaseLeadLabel = (caseLead: CaseLead) => {
  const caseLeadIsAssignable = caseLead?.accounts?.some((account) =>
    account.Roles?.some((role) =>
      role?.SystemPermissions?.some(
        // eslint-disable-next-line max-nested-callbacks
        (systemPermission) =>
          systemPermission.permission ===
            SYSTEM_PERMISSIONS.ASSIGNABLE_AS_CASE_LEAD ||
          systemPermission.permission ===
            SYSTEM_PERMISSIONS.ASSIGNABLE_AS_DISCLOSURES_CASE_LEAD,
      ),
    ),
  );

  let deactivatedSuffix = '';
  if (caseLeadIsAssignable !== undefined) {
    deactivatedSuffix = caseLeadIsAssignable ? '' : ' (Deactivated)';
  }

  const caseLeadLabel =
    (caseLead ? caseLead.name : 'Unassigned') + deactivatedSuffix;

  return caseLeadLabel;
};

export const getCaseLeadId = (caseLead?: Maybe<Partial<UserType>>) => {
  return caseLead?.id ?? -1;
};
